// lint-internal-leaks-ignore-next-line: framework-script — load-bearing visual regression marker
// @visual-diff-render: vite
/**
 * @file AssessmentNav — top chrome for an assessment-taking flow
 * @version 0.2.1
 */

import React, { forwardRef, type ElementType, type HTMLAttributes } from 'react';
import { tokens } from '../../../theme/tokens';
import { Logo } from '../Logo';
import { AssessmentProgress } from '../AssessmentProgress';
import styles from './AssessmentNav.module.css';

/*
 * AssessmentNav — Top chrome for an assessment-taking flow.
 *
 * Renders a brand mark, an assessment-name label, and the live completion
 * progress. The chassis adapts across a viewport-driven size axis (md ≥768
 * / sm <768) and a brand prop axis (mayo / h&s):
 *
 *   - md: single-row horizontal flex with full AssessmentProgress on right
 *   - sm + mayo: same single-row layout, AssessmentProgress's bar dropped,
 *                only the "X%" label remains on the right
 *   - sm + h&s: 2-row vertical stack (wide H&S logo on top, name + "X%" on
 *               row 2 with space-between)
 *
 * Responsive behavior is pure CSS — no matchMedia, no useEffect, no JS
 * state. Drop into any layout that resizes; the chassis adapts.
 *
 *   <AssessmentNav assessmentName="Business Impact Inventory" percent={10} />
 *   <AssessmentNav assessmentName="Business Impact Inventory" percent={45} brand="hs" />
 */

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

export interface AssessmentNavProps extends HTMLAttributes<HTMLElement> {
  /** The assessment title — rendered as a static text label. */
  assessmentName: React.ReactNode;
  /** Plain-text title used for accessible progress labels when the title is rich content. */
  assessmentNameText?: string | React.ReactNode;
  /** Completion percentage 0–100. Drives the visible "X%" label at all sizes
   *  and the AssessmentProgress fill width at `md` viewports. Clamped to
   *  `[0, 100]`. */
  percent: number;
  /** Brand mark selector. Default `mayo`. The H&S logo is wider and forces a
   *  2-row stack at `sm` viewports. */
  brand?: 'mayo' | 'hs' | string;
  /** Explicit chassis width — overrides the default `100%`. Useful for
   *  pinning renders at the source design widths (1988 / 375). */
  width?: number | string;
  /** Frosted-glass state. When true, the nav fades to a translucent navy tint +
   *  backdrop blur + hairline bottom rule, so content scrolled UNDER the sticky
   *  nav reads as obscured. Hosts drive this from their own scroll position
   *  (true once anything is scrolled beneath the nav, false at the top). */
  scrolled?: boolean;
  /**
   * Colour treatment. `dark` (default) is the white-recoloured logo + light ink
   * for dark surfaces; `light` keeps the logo in its native brand colour and
   * uses dark-blue ink + grey hairlines for the light-surface flow. Threads
   * through to the embedded AssessmentProgress. A per-instance variant.
   */
  theme?: 'light' | 'dark';
  /** Render the root as a different element/component. Default `'header'`.
   *  Landmark-ness (e.g. `role="banner"`) is the consumer's responsibility —
   *  this primitive no longer assumes it's the page's outermost header. */
  as?: ElementType;
  /** Optional custom header logo. Accepts:
   *   - a React element (e.g. `<MyLogo />`)
   *   - a component type (e.g. `MyLogoComponent`) which will be invoked with
   *     `{ brand }` props
   *   - a string path to an image (e.g. `/assets/logo.png`)
   *  If omitted the built-in `Logo` component is used. */
  headerLogo?: React.ReactNode | React.ComponentType<{ brand?: string }> | string;
}

export const AssessmentNav = forwardRef<HTMLElement, AssessmentNavProps>(
  function AssessmentNav(
    { assessmentName, assessmentNameText, percent, brand = 'mayo', width = '100%', className, scrolled = false, theme = 'dark', as, style, headerLogo, ...rest },
    ref,
  ) {
  const Component = as ?? 'header';
  const safePercent = clamp(percent, 0, 100);
  const labelText = `${Math.round(safePercent)}%`;

  const titleStyle: React.CSSProperties = {
    fontFamily: tokens.font['family-avenir'],
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '24px',
    color: 'var(--hd-c-assessmentnav-title-fg)',
    whiteSpace: 'nowrap',
  };
  const percentStyle: React.CSSProperties = {
    fontFamily: tokens.font['family-avenir'],
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '24px',
    color: 'var(--hd-c-assessmentprogress-label-fg)',
    whiteSpace: 'nowrap',
  };

  return (
    <Component
      ref={ref}
      className={[styles.root, theme === 'light' && styles.light, scrolled && styles.scrolled, className].filter(Boolean).join(' ')}
      style={{ width, ...style }}
      {...rest}
    >
      <div className={styles.chassis} data-brand={brand}>
        <div className={styles.leftCluster}>
          <div className={styles.logoBlock}>
            {headerLogo ?  typeof headerLogo === 'string' ? (
                <img src={headerLogo} style={{ maxWidth: '200px', maxHeight: '40px' }} alt='logo' />
              ) : typeof headerLogo === 'function' ? (
                React.createElement(headerLogo as React.ComponentType<{ brand?: string }>, { brand })
              ) : (
                <Logo brand={brand} />
              )
             : (
              <Logo brand={brand} />
            )}
            <span className={styles.logoDivider} aria-hidden="true" />
          </div>
          <span className={styles.title} style={titleStyle}>
            {assessmentName}
          </span>
        </div>

        <div className={styles.rightSlot}>
          <span className={styles.fullProgress}>
            <AssessmentProgress percent={safePercent} theme={theme} aria-label={`${assessmentNameText ?? ''} progress`} />
          </span>
          <span className={styles.percentLabel} style={percentStyle} aria-hidden="true">
            {labelText}
          </span>
        </div>

        <div className={styles.divider} aria-hidden="true" />

        <div className={styles.secondRow}>
          <span className={styles.secondRowTitle} style={titleStyle}>
            {assessmentName}
          </span>
          <span className={styles.secondRowPercent} style={percentStyle} aria-hidden="true">
            {labelText}
          </span>
        </div>
      </div>
    </Component>
  );
  },
);

AssessmentNav.displayName = 'AssessmentNav';
